import * as React from "react";

import { ITag, mergeFuncStyles } from "@bps/fluent-ui";

import { ConvertToSinglePickerProps } from "./ExtendedPicker.types.ts";
import { extendToSinglePicker } from "./extendToSinglePicker.tsx";
import { GenericMultiTagPickerProps } from "./MultiTagPicker.tsx";

type InnerProps<P> = ConvertToSinglePickerProps<P, ITag>;

/**
 * HOC that takes a tag item based picker and turns it into a single selection picker.
 * @param Component
 */
export const extendToSingleTagPicker = <
  P extends Pick<
    GenericMultiTagPickerProps<TItem>,
    | "onChange"
    | "componentRef"
    | "styles"
    | "onInputChange"
    | "onRenderItem"
    | "withTagItemProps"
  >,
  TItem
>(
  Component: React.FunctionComponent<P>
): React.FunctionComponent<ConvertToSinglePickerProps<P, ITag>> => {
  const SinglePickerComponent = extendToSinglePicker<P, ITag, any>(
    Component,
    props => ({
      // when the picker takes focus, we want to show the selected item as a ghost value behind the input
      // Whenever the user starts typing (state.isInputEmpty === false), the ghost text needs to be invisible.
      itemsWrapper: [
        {
          "& > .ms-TagItem": {
            position: "initial"
          },
          "& > .ms-TagItem .ms-TagItem-close": {
            position: "absolute",
            right: 0,
            top: 0
          }
        },
        {
          "& > .ms-TagItem .ms-TagItem-text": {
            paddingRight: 32
          }
        },
        props.isFocused && {
          "& > .ms-TagItem .ms-TagItem-text": {
            opacity: props.isInputEmpty ? 1 : 0
          }
        }
      ]
    })
  );

  return (props: InnerProps<P>) => {
    const withTagItemProps: GenericMultiTagPickerProps<TItem>["withTagItemProps"] =
      item => {
        const otherProps = props.withTagItemProps
          ? props.withTagItemProps(item)
          : {};

        const styles = mergeFuncStyles(({ theme: { palette } }) => {
          return {
            text: {
              /** replacing margin with padding to be able to intercept click events */
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: 8,
              paddingRight: 8,
              minWidth: 22
            },
            root: {
              background: palette.white,
              color: palette.neutralPrimary,
              selectors: {
                ":hover": {
                  background: palette.white
                }
              }
            },
            close: {
              color: palette.neutralSecondary,
              background: palette.white,
              selectors: {
                "&:hover": {
                  color: palette.neutralPrimary,
                  background: palette.neutralLighter
                },
                "&:active": {
                  color: palette.neutralPrimary,
                  background: palette.neutralLighter
                }
              }
            }
          };
        }, otherProps.styles);
        return {
          ...otherProps,
          styles,
          disableFocus: true
        };
      };

    return (
      <SinglePickerComponent
        {...(props as InnerProps<P>)}
        withTagItemProps={withTagItemProps}
      />
    );
  };
};
